import {
  SearchBar,
  type SearchBarWhenProps,
  type SearchBarWhereProps,
  type SearchMember,
  type WhoSelection,
} from "@ui/components/SearchBar";
import type { ReactNode } from "react";

export interface ProgramsIndexViewProps {
  /** SearchBar "Where" section props (location search + selection). */
  whereProps: SearchBarWhereProps;
  /** SearchBar "When" section props (date-window selection). */
  whenProps: SearchBarWhenProps;
  /** SearchBar "Who" selection value + change handler (family-member picker). */
  whoProps: { value: WhoSelection; onChange: (value: WhoSelection) => void };
  /** Whether the current account is a family account (drives the Who member list vs. upsell). */
  isFamily: boolean;
  /** Whether the viewer already owns a family account to switch to (vs. needing to create one). */
  hasFamilyAccount: boolean;
  /** Family members available in the Who picker. */
  members: SearchMember[];
  /** Start the "create a family account" flow from the Who upsell. */
  onCreateFamily: () => void;
  /** Switch to the viewer's existing family account. Omit to hide the switch action. */
  onSwitchToFamily?: () => void;
  /** Opens the add-someone sheet from the Who picker. Omit to hide the add row. */
  onAddPerson?: () => void;
  /** The add-someone sheet itself — a slot so this view stays presentational. */
  addPersonSheet?: ReactNode;
  /** Connected filters body rendered inside the Filters modal. */
  filters: ReactNode;
  /** Sticky Filters-modal footer; receives a `close` callback. */
  renderFiltersFooter: (close: () => void) => ReactNode;
}

/**
 * Presentational discovery chrome for the /programs index: the desktop-only
 * SearchBar (Where / When / Who sections + Filters modal) that lives in the
 * TopBar's `bottomContent` slot so it persists across scrolls. All search state,
 * routing and connected filter children are wired by the container; this
 * component only lays out the bar. Hidden below `lg` — discovery chrome is
 * desktop-only on /programs today.
 */
export function ProgramsIndexView({
  whereProps,
  whenProps,
  whoProps,
  isFamily,
  hasFamilyAccount,
  members,
  onCreateFamily,
  onSwitchToFamily,
  onAddPerson,
  addPersonSheet,
  filters,
  renderFiltersFooter,
}: ProgramsIndexViewProps) {
  return (
    <div className="hidden border-neutral-100 border-b bg-white px-5 pb-6 md:px-8 lg:block">
      <SearchBar>
        <SearchBar.Sections>
          <SearchBar.Where {...whereProps} />
          <SearchBar.When {...whenProps} />
          <SearchBar.Who
            {...whoProps}
            hasFamilyAccount={hasFamilyAccount}
            isFamily={isFamily}
            members={members}
            onAddPerson={onAddPerson}
            onCreateFamily={onCreateFamily}
            onSwitchToFamily={onSwitchToFamily}
          />
        </SearchBar.Sections>
        <SearchBar.Filters footer={renderFiltersFooter} size="lg">
          {filters}
        </SearchBar.Filters>
      </SearchBar>
      {addPersonSheet}
    </div>
  );
}
